import {useEffect, useRef, useState} from "react";
import {getTurnstileConfig} from "../../../services/AuthService";

const SCRIPT_ID = "cloudflare-turnstile-script";
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise;

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const script = existing || document.createElement("script");
    const handleLoad = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile unavailable"));
    };
    script.addEventListener("load", handleLoad, {once: true});
    script.addEventListener("error", reject, {once: true});
    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }).catch((error) => {
    scriptPromise = undefined;
    document.getElementById(SCRIPT_ID)?.remove();
    throw error;
  });

  return scriptPromise;
};

export default function TurnstileWidget({
  onTokenChange,
  onRequirementChange,
  resetKey = 0,
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [message, setMessage] = useState("Chargement de la protection anti-robot…");
  const [failed, setFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let active = true;
    onRequirementChange?.(true);
    onTokenChange?.(null);
    setFailed(false);
    getTurnstileConfig()
      .then(({data}) => {
        if (!active) return;
        const publicConfig = data?.data || {};
        if (!publicConfig.enabled) {
          onRequirementChange?.(false);
          setMessage("");
          return;
        }
        if (!publicConfig.configured || !publicConfig.siteKey) {
          setFailed(true);
          setMessage("La protection anti-robot est indisponible. Veuillez réessayer plus tard.");
          return;
        }
        setConfig(publicConfig);
      })
      .catch(() => {
        if (active) {
          setFailed(true);
          setMessage("La protection anti-robot est indisponible. Veuillez réessayer plus tard.");
        }
      });

    return () => {
      active = false;
    };
  }, [onRequirementChange, onTokenChange, retryKey]);

  useEffect(() => {
    if (!config?.enabled || !containerRef.current) return undefined;
    let active = true;
    onTokenChange?.(null);
    setMessage("Chargement de la protection anti-robot…");

    loadTurnstile()
      .then((turnstile) => {
        if (!active || !containerRef.current) return;
        if (widgetIdRef.current !== null) turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: config.siteKey,
          action: "otp_send",
          theme: "auto",
          size: "flexible",
          callback: (token) => {
            setMessage("");
            onTokenChange?.(token);
          },
          "expired-callback": () => {
            setMessage("La vérification a expiré. Veuillez la refaire.");
            onTokenChange?.(null);
          },
          "error-callback": () => {
            setFailed(true);
            setMessage("La vérification anti-robot a échoué. Veuillez réessayer.");
            onTokenChange?.(null);
          },
        });
      })
      .catch(() => {
        if (active) {
          setFailed(true);
          setMessage("La protection anti-robot est indisponible. Veuillez réessayer.");
        }
      });

    return () => {
      active = false;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [config, onTokenChange, resetKey]);

  if (config === null && message === "") return null;

  return (
    <div className="my-3 w-full" aria-live="polite">
      <div ref={containerRef} className="min-h-[65px] w-full" />
      {message && <div className="mt-1 text-sm text-[#667085]">{message}</div>}
      {failed && (
        <button
          type="button"
          className="mt-1 text-sm font-medium text-[#1A64B9] underline"
          onClick={() => {
            setConfig(null);
            setRetryKey((current) => current + 1);
          }}
        >
          Réessayer
        </button>
      )}
    </div>
  );
}
